import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { TreeSelect, Select } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';

const DEFAULT_PLACEHOLDER = 'Select an option';
const DEFAULT_LABEL_FIELD = 'name';
const DEFAULT_VALUE_FIELD = 'id';
const DEFAULT_CHILDREN_FIELD = 'children';
const DEFAULT_SEARCH_PARAM = 'search';
const DEFAULT_PARENT_PARAM = 'parent_id';
const DEFAULT_LANGUAGE = 'fr';
const DEBOUNCE_DELAY = 300;
const LANGUAGE_CHANGE_DELAY = 100;
const DROPDOWN_MAX_HEIGHT = 400;
const DROPDOWN_PADDING = '8px 0';
const CHEVRON_SIZE = 14;
const CultureTreeSelect = ({
  label,
  endpoint,
  placeholder = DEFAULT_PLACEHOLDER,
  isMulti = false,
  labelField = DEFAULT_LABEL_FIELD,
  valueField = DEFAULT_VALUE_FIELD,
  childrenField = DEFAULT_CHILDREN_FIELD,
  searchParam = DEFAULT_SEARCH_PARAM,
  parentIdParam = DEFAULT_PARENT_PARAM,
  value,
  onChange,
  style,
  checkStrictly = false,
  languageFields = null,
  languageLabels = null,
  defaultLanguage = DEFAULT_LANGUAGE,
  showLanguageSelector = false,
  onLanguageChange = null,
}) => {
  const { t } = useTranslation();
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [error, setError] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [loadedKeys, setLoadedKeys] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    if (!languageFields) return null;
    const saved = localStorage.getItem(`lang_${endpoint}_${labelField}`);
    return saved || defaultLanguage;
  });

  const currentLabelField = useMemo(() => {
    return languageFields?.[selectedLanguage] || labelField;
  }, [languageFields, selectedLanguage, labelField]);

  const getLanguageOptions = (languageLabels) => 
    Object.entries(languageLabels).map(([code, label]) => ({
      value: code,
      label: label,
    }));

  const LanguageSelector = ({ selectedLanguage, languageLabels, onChange }) => (
    <Select
      size="small"
      value={selectedLanguage}
      onChange={onChange}
      options={getLanguageOptions(languageLabels)}
      className="language-selector"
      suffixIcon={<GlobalOutlined />}
    />
  );

  const fetchTreeData = useCallback(async (url) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const { data } = await response.json();
      return data || [];
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch tree data:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const transformTreeData = useCallback(
    (data) => {
      return data.map((item) => ({
        ...item,
        key: item[valueField] || item.id,
        [currentLabelField]: item[currentLabelField] || item[labelField],
        title: item[currentLabelField] || item[labelField],
        value: item[valueField] || item.id,
        isLeaf: !item.has_children,
      }));
    },
    [valueField, currentLabelField, labelField]
  );

  const fetchRootNodes = useCallback(async () => {
    const data = await fetchTreeData(endpoint);
    const transformedData = transformTreeData(data);
    setTreeData(transformedData);
  }, [endpoint, fetchTreeData, transformTreeData]);

  const buildSearchUrl = (searchTerm) => {
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set(searchParam, encodeURIComponent(searchTerm));
    return url.toString();
  };

  const fetchSearchResults = useCallback(
    async (searchTerm) => {
      const url = buildSearchUrl(searchTerm);
      const data = await fetchTreeData(url);
      const transformedData = transformTreeData(data);
      setTreeData(transformedData);
    },
    [endpoint, searchParam, fetchTreeData, transformTreeData]
  );

  const transformChildNode = (child) => ({
    ...child,
    key: child[valueField] || child.id,
    title:
      child[currentLabelField] ||
      child[labelField] ||
      child.french_name ||
      child.name,
    value: child[valueField] || child.id,
    isLeaf: !child.has_children,
  });

  const updateTreeData = useCallback(
    (list, key, children) => {
      return list.map((node) => {
        if (node[valueField] === key) {
          return {
            ...node,
            [childrenField]: children.map(transformChildNode),
          };
        }
        if (node[childrenField]) {
          return {
            ...node,
            [childrenField]: updateTreeData(node[childrenField], key, children),
          };
        }
        return node;
      });
    },
    [childrenField, currentLabelField, labelField, valueField]
  );

  const buildChildrenUrl = (nodeValue, includeLanguage = false) => {
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set(parentIdParam, nodeValue);
    if (includeLanguage && selectedLanguage && languageFields) {
      url.searchParams.set('language', selectedLanguage);
    }
    return url.toString();
  };

  const preloadNodeChildren = useCallback(
    async (nodeValue) => {
      try {
        const url = buildChildrenUrl(nodeValue);
        const children = await fetchTreeData(url);

        if (children && children.length > 0) {
          setTreeData((prev) => updateTreeData(prev, nodeValue, children));
        }

        return children;
      } catch (err) {
        console.error('Failed to preload children:', err);
        return [];
      }
    },
    [endpoint, parentIdParam, fetchTreeData, updateTreeData]
  );

  const onLoadData = useCallback(
    async (node) => {
      if (node.children?.length > 0) return;

      try {
        const url = buildChildrenUrl(node.value, true);
        const children = await fetchTreeData(url);

        setTreeData((prev) => updateTreeData(prev, node.value, children));
        setLoadedKeys((prev) => [...prev, node.value]);
      } catch (err) {
        setError(t('common|Failed to load children'));
      }
    },
    [
      endpoint,
      parentIdParam,
      fetchTreeData,
      t,
      selectedLanguage,
      languageFields,
    ]
  );

  const findNodeInTree = useCallback(
    (tree, targetValue) => {
      for (const node of tree) {
        if (node[valueField] === targetValue || node.value === targetValue) {
          return node;
        }
        if (node[childrenField] && node[childrenField].length > 0) {
          const found = findNodeInTree(node[childrenField], targetValue);
          if (found) return found;
        }
      }
      return null;
    },
    [valueField, childrenField]
  );

  const getNodeTitle = (node, newLabelField) => 
    node[newLabelField] ||
    node[labelField] ||
    node.french_name ||
    node.name;

  const updateNodeTitles = (nodes, newLabelField) => {
    return nodes.map((node) => ({
      ...node,
      title: getNodeTitle(node, newLabelField),
      [childrenField]: node[childrenField]
        ? updateNodeTitles(node[childrenField], newLabelField)
        : node[childrenField],
    }));
  };

  const clearSelections = () => {
    if (onChange) {
      onChange([], []);
    }
  };

  const triggerRevalidation = () => {
    if (onLanguageChange) {
      setTimeout(() => onLanguageChange(), LANGUAGE_CHANGE_DELAY);
    }
  };

  const handleLanguageChange = useCallback(
    (lang) => {
      setSelectedLanguage(lang);
      localStorage.setItem(`lang_${endpoint}_${labelField}`, lang);
      setSearchValue('');

      const newLabelField = languageFields?.[lang] || labelField;

      setTreeData((prev) => updateNodeTitles(prev, newLabelField));
      clearSelections();
      triggerRevalidation();
    },
    [endpoint, labelField, onChange, languageFields, childrenField, onLanguageChange]
  );

  const normalizeValues = (selectedValues) => 
    Array.isArray(selectedValues)
      ? selectedValues
      : [selectedValues].filter(Boolean);

  const findExistingNode = (nodes, value) => 
    nodes.find(
      (node) =>
        node && (node.value === value || node[valueField] === value)
    );

  const buildNodeUrl = (value) => {
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('id', value);
    return url.toString();
  };

  const createEnrichedNode = (existingNode, value) => ({
    value: existingNode[valueField] || existingNode.value || value,
    label:
      existingNode[currentLabelField] ||
      existingNode[labelField] ||
      existingNode.title ||
      String(value),
    key: existingNode[valueField] || existingNode.value || value,
    title:
      existingNode[currentLabelField] ||
      existingNode[labelField] ||
      existingNode.title ||
      String(value),
    ...existingNode,
  });

  const createFallbackNode = (value) => ({
    value: value,
    label: String(value),
  });

  const enrichNode = async (value, nodes) => {
    let existingNode = findExistingNode(nodes, value);

    if (!existingNode) {
      existingNode = findNodeInTree(treeData, value);
    }

    if (!existingNode) {
      try {
        const url = buildNodeUrl(value);
        const response = await fetchTreeData(url);
        if (response && response.length > 0) {
          existingNode = response[0];
          await preloadNodeChildren(value);
        }
      } catch (error) {
        console.error('Failed to fetch node data:', error);
      }
    }

    return existingNode 
      ? createEnrichedNode(existingNode, value)
      : createFallbackNode(value);
  };

  const handleChange = useCallback(
    async (selectedValues, selectedNodes) => {
      if (!onChange) return;

      const validValues = normalizeValues(selectedValues);
      const nodes = selectedNodes || [];

      const enrichedNodes = await Promise.all(
        validValues.map(value => enrichNode(value, nodes))
      );

      onChange(validValues, enrichedNodes);
    },
    [
      onChange,
      valueField,
      currentLabelField,
      labelField,
      findNodeInTree,
      treeData,
      endpoint,
      fetchTreeData,
    ]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue?.trim()) {
        fetchSearchResults(searchValue);
      } else {
        fetchRootNodes();
      }
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [searchValue, fetchSearchResults, fetchRootNodes]);

  useEffect(() => {
    if (languageFields && selectedLanguage) {
      setTreeData([]);
      setSearchValue('');
      fetchRootNodes();
    }
  }, [currentLabelField]);

  return (
    <div className="w-full space-y-2">
      <div className="select-header flex items-center justify-between">
        {label && (
          <label className="block text-sm font-medium text-gray-700">
            {label}
          </label>
        )}
        {showLanguageSelector && languageFields && languageLabels && (
          <LanguageSelector
            selectedLanguage={selectedLanguage}
            languageLabels={languageLabels}
            onChange={handleLanguageChange}
          />
        )}
      </div>

      <TreeSelect
        key={`${selectedLanguage}-${currentLabelField}`}
        value={value}
        onChange={handleChange}
        treeData={treeData}
        loadData={onLoadData}
        expandedKeys={expandedKeys}
        onExpand={setExpandedKeys}
        loadedKeys={loadedKeys}
        treeNodeFilterProp="title"
        fieldNames={{
          label: 'title',
          value: 'value',
          children: childrenField,
          key: 'key',
        }}
        switcherIcon={({ expanded }) =>
          expanded ? <ChevronDown size={CHEVRON_SIZE} /> : <ChevronRight size={CHEVRON_SIZE} />
        }
        placeholder={placeholder}
        loading={loading}
        showSearch
        allowClear
        multiple={isMulti}
        treeDefaultExpandAll={false}
        style={style}
        dropdownStyle={{
          maxHeight: DROPDOWN_MAX_HEIGHT,
          overflow: 'auto',
          padding: DROPDOWN_PADDING,
        }}
        searchValue={searchValue}
        onSearch={setSearchValue}
        filterTreeNode={(inputValue, treeNode) => {
          return String(treeNode.title || treeNode[currentLabelField])
            .toLowerCase()
            .includes(inputValue.toLowerCase());
        }}
        notFoundContent={
          error ||
          (loading ? t('common|Loading...') : t('common|No options found'))
        }
        treeCheckable={isMulti}
        showCheckedStrategy={TreeSelect.SHOW_ALL}
        dropdownMatchSelectWidth={false}
        checkStrictly={checkStrictly}
      />
    </div>
  );
};

export default CultureTreeSelect;
